import { useState } from "react";
import { Checkbox, FormControlLabel, Link, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import { match } from "ts-pattern";

import type { TermsAndConditions, UUID } from "@thalamos/aspire-common";

import {
  Button,
  LoadingSpinner,
  PopupDialog,
  PopupDialogTitle,
} from "../../design-system/index.js";
import { getTermsAndConditionsLabel } from "./termsAndConditionsLabels.js";
import { apiHooks } from "~/api.js";

export type TermsAndConditionsModalInnerProps = {
  termsAndConditions: TermsAndConditions;
  onClose: () => void;
  needsAcceptance: boolean;
  open: boolean;
};

export function TermsAndConditionsModalInner({
  termsAndConditions,
  onClose,
  needsAcceptance,
  open,
}: TermsAndConditionsModalInnerProps) {
  const { t } = useTranslation();
  const [checked, setChecked] = useState(false);

  const label = needsAcceptance
    ? t("buttonLabels.continue")
    : t("buttonLabels.close");

  return (
    <PopupDialog
      title={t("pages.patientEditCreate.updateSuccess")}
      open={open}
      onClose={() => {}}
      zIndex={1301} // by default, the zIndex is 1300 for modals, so setting it to 1301 so this modal always appears on top
    >
      {!needsAcceptance && (
        <PopupDialogTitle
          closeDialog={onClose}
          titleText={getTermsAndConditionsLabel(termsAndConditions.type)}
        ></PopupDialogTitle>
      )}
      <Markdown>{termsAndConditions.markdown}</Markdown>
      {needsAcceptance && (
        <FormControlLabel
          control={
            <Checkbox
              // @ts-expect-error data-* attributes reach the DOM but are absent from
              // React's InputHTMLAttributes, so an object literal fails the excess
              // property check.
              inputProps={{ "data-testid": "terms-and-conditions-checkbox" }}
              checked={checked}
              onClick={() => setChecked(!checked)}
            />
          }
          label={
            <Typography variant="body1">
              <Trans
                i18nKey={match(termsAndConditions.type)
                  .with(
                    "terms-of-use",
                    () =>
                      "layout.termsAndConditionsModal.confirmationTextTermsOfUse",
                  )
                  .with(
                    "data-processing-agreement",
                    () => "layout.termsAndConditionsModal.confirmationText",
                  )
                  .otherwise(() => "")}
                components={{
                  1: (
                    <Link
                      href="https://www.thalamos.co.uk/terms-of-use/"
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                    />
                  ),
                }}
              />
            </Typography>
          }
        />
      )}
      <Button
        testId="continue-terms-and-conditions"
        label={label}
        onClick={() => {
          onClose();
        }}
        disabled={!checked && needsAcceptance}
      />
    </PopupDialog>
  );
}

export function TermsAndConditionsModal(props: {
  termsAndConditionsId: UUID;
  onClose: () => void;
  needsAcceptance: boolean;
  open: boolean;
}) {
  const { t } = useTranslation();
  const [{ data: termsAndConditions, loading: loadingTermsAndConditions }] =
    apiHooks.termsAndConditions.get(props.termsAndConditionsId);

  if (loadingTermsAndConditions) {
    return (
      <PopupDialog title="Loading" open={props.open} onClose={() => {}}>
        <LoadingSpinner />
      </PopupDialog>
    );
  }

  // Terms with any other id are left over from an earlier request, and showing
  // them for acceptance would record agreement to terms the user never saw.
  const requestedTermsAndConditions =
    termsAndConditions?.id === props.termsAndConditionsId
      ? termsAndConditions
      : undefined;

  if (requestedTermsAndConditions) {
    return (
      <TermsAndConditionsModalInner
        termsAndConditions={requestedTermsAndConditions}
        onClose={props.onClose}
        needsAcceptance={props.needsAcceptance}
        open={props.open}
      />
    );
  }

  return (
    <PopupDialog open={props.open} onClose={() => {}}>
      <PopupDialogTitle
        titleText={t("errors.somethingWentWrong")}
        subtitleText={t("errors.somethingWentWrongDescription")}
        closeDialog={props.onClose}
        // With needsAcceptance, onClose means "accepted"; terms that never
        // loaded cannot be accepted.
        hideCloseButton={props.needsAcceptance}
      />
    </PopupDialog>
  );
}
